(() => {
  if (window.kornerProductImageSync) return;
  window.kornerProductImageSync = true;
  const root = document.documentElement;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cards = new Map();
  const seen = new WeakSet();
  const interval = 2000;
  let origin = performance.now();
  let timer;
  let paused = motion.matches;
  let control;
  const updateControl = () => {
    if (!control) return;
    control.hidden = cards.size === 0;
    control.setAttribute('aria-pressed', String(paused));
    control.querySelector('[data-korner-image-control-label]').textContent =
      paused ? control.dataset.resumeLabel : control.dataset.pauseLabel;
  };
  const load = (card) => {
    if (card.started) return;
    card.started = true;
    const image = card.secondary;
    image.loading = 'eager';
    image.addEventListener('load', async () => {
      try { await image.decode(); } catch (_) {}
      card.loaded = image.naturalWidth > 0;
    }, { once: true });
    image.addEventListener('error', () => {
      card.loaded = false;
      card.host.removeAttribute('data-korner-image-ready');
    });
    image.srcset = card.srcset;
    image.src = card.src;
    if (image.complete && image.naturalWidth > 0) {
      image.decode().then(() => { card.loaded = true; }).catch(() => {});
    }
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      const card = cards.get(entry.target);
      if (card && entry.isIntersecting) load(card);
    }
  }, { rootMargin: '300px' }) : null;
  const register = (scope) => {
    const templates = scope.matches?.('template[data-korner-image-sync]')
      ? [scope] : scope.querySelectorAll?.('template[data-korner-image-sync]') || [];
    for (const template of templates) {
      if (seen.has(template)) continue;
      seen.add(template);
      const host = template.parentElement;
      if (cards.has(host)) continue;
      const primary = Array.from(host.children).find(el => el.tagName === 'IMG' && !el.hasAttribute('data-korner-image-secondary'));
      const source = template.content.querySelector('img');
      if (!primary || !source) continue;
      for (const stale of host.querySelectorAll(':scope > [data-korner-image-secondary]')) stale.remove();
      host.removeAttribute('data-korner-image-ready');
      const secondary = source.cloneNode(true);
      const src = secondary.getAttribute('src');
      const srcset = secondary.getAttribute('srcset') || '';
      secondary.removeAttribute('src');
      secondary.removeAttribute('srcset');
      secondary.className = primary.className.split(/\s+/).filter(name => !['lazyload', 'lazyloading', 'lazyloaded', 'fade-in'].includes(name)).join(' ');
      secondary.setAttribute('data-korner-image-secondary', '');
      secondary.setAttribute('aria-hidden', 'true');
      secondary.sizes = primary.getAttribute('sizes') || `${Math.max(host.getBoundingClientRect().width, 320)}px`;
      const style = getComputedStyle(primary);
      secondary.style.setProperty('object-fit', style.objectFit, 'important');
      secondary.style.setProperty('object-position', style.objectPosition, 'important');
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      primary.setAttribute('data-korner-image-primary', '');
      host.setAttribute('data-korner-image-host', '');
      primary.after(secondary);
      const card = { host, primary, secondary, src, srcset, loaded: false, started: false };
      cards.set(host, card);
      if (observer) observer.observe(host); else load(card);
    }
    updateControl();
  };
  const tick = () => {
    clearTimeout(timer);
    for (const [host, card] of cards) {
      if (!host.isConnected) {
        observer?.unobserve(host);
        cards.delete(host);
      } else if (card.loaded) {
        host.setAttribute('data-korner-image-ready', '');
      }
    }
    const elapsed = performance.now() - origin;
    root.setAttribute('data-korner-image-frame', paused ? '0' : String(Math.floor(elapsed / interval) % 2));
    updateControl();
    if (!paused && !document.hidden) timer = window.setTimeout(tick, interval - elapsed % interval);
  };
  const start = () => {
    control = document.querySelector('[data-korner-image-control]');
    register(document);
    new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) {
        if (node.nodeType === 1) register(node);
      }
    }).observe(document.body, { childList: true, subtree: true });
    control?.addEventListener('click', () => {
      paused = !paused;
      origin = performance.now();
      tick();
    });
    motion.addEventListener('change', event => {
      paused = event.matches;
      origin = performance.now();
      tick();
    });
    document.addEventListener('visibilitychange', tick);
    origin = performance.now();
    tick();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
